import React from 'react';
import type { Dayjs } from 'dayjs';
import { BaseActivityMessage } from '../BaseActivityMessage';
import { DateTimePicker } from '../DateTimePicker';

export interface DateTimeActivityMessageProps {
  /**
   * Current date-time value (ISO string format)
   */
  value: string;
  /**
   * Callback when date-time changes
   */
  onChange: (value: string | null) => void;
  /**
   * Callback when submit button is clicked
   */
  onSubmit: () => void;
  /**
   * Whether to show skip button
   */
  allowSkip?: boolean;
  /**
   * Skip button text
   */
  skipText?: string;
  /**
   * Submit button text
   */
  submitText?: string;
  /**
   * Callback when skip button is clicked
   */
  onSkip?: () => void;
  /**
   * Minimum allowed date (ISO string, YYYY-MM-DD format)
   */
  minDate?: string;
  /**
   * Maximum allowed date (ISO string, YYYY-MM-DD format)
   */
  maxDate?: string;
  /**
   * Minimum allowed time (ISO string)
   */
  minTime?: string;
  /**
   * Maximum allowed time (ISO string)
   */
  maxTime?: string;
  /**
   * Whether the inputs are disabled
   */
  disabled?: boolean;
  /**
   * Label for the date-time picker
   */
  label?: string;
  /**
   * Minutes step interval
   */
  minutesStep?: number;
  /**
   * Function to determine if a specific date should be disabled
   * Receives a Dayjs object
   */
  shouldDisableDate?: (date: Dayjs) => boolean;
  /**
   * Locale for date-time picker (e.g., 'en', 'de')
   * Note: The DateTimePicker component handles localization internally
   */
  locale?: string;
  /**
   * Whether the submit button should be enabled
   */
  submitEnabled?: boolean;
  /**
   * Message content to display above the date-time picker
   */
  children?: React.ReactNode;
  /**
   * Sender type: 'bot' or 'user'
   */
  sender: 'bot' | 'user';
  /**
   * Avatar URL for the sender
   */
  avatarUrl?: string;
  /**
   * Timestamp for the message
   */
  timestamp?: string;
  /**
   * Custom bot display name
   */
  botName?: string;
}

/**
 * DateTimeActivityMessage displays a date and time selection interface with submit and optional skip buttons.
 * This component is designed for interactive date-time selection in chat-like interfaces,
 * with support for localization, date/time constraints, and custom validation.
 */
export const DateTimeActivityMessage: React.FC<DateTimeActivityMessageProps> = ({
  value,
  onChange,
  onSubmit,
  allowSkip = false,
  skipText = 'Skip',
  submitText = 'Send',
  onSkip,
  minDate,
  maxDate,
  minTime,
  maxTime,
  disabled = false,
  label = '',
  minutesStep,
  shouldDisableDate,
  submitEnabled = true,
  children,
  sender,
  avatarUrl,
  timestamp,
  botName,
}) => {
  return (
    <BaseActivityMessage
      allowSkip={allowSkip}
      skipText={skipText}
      submitText={submitText}
      onSkip={onSkip}
      disabled={disabled}
      submitEnabled={submitEnabled}
      sender={sender}
      avatarUrl={avatarUrl}
      timestamp={timestamp}
      botName={botName}
      onSubmit={onSubmit}
      renderInput={() => (
        <DateTimePicker
          value={value && typeof value === 'string' ? value : ''}
          onChange={onChange}
          maxDate={maxDate}
          minDate={minDate}
          maxTime={maxTime}
          minTime={minTime}
          minutesStep={minutesStep}
          disabled={disabled}
          label={label}
          shouldDisableDate={shouldDisableDate}
        />
      )}
    >
      {children}
    </BaseActivityMessage>
  );
};
